/**
 * @file Breadcrumb 面包屑
 * @description 层级导航路径。
 *
 * @whenToUse 详情页、多级后台页面顶部。
 * @whenNotToUse 侧栏主导航。
 */

import { Fragment, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type BreadcrumbItem = {
  key: string
  label: ReactNode
  href?: string
  onClick?: () => void
}

export type BreadcrumbProps = {
  items: BreadcrumbItem[]
  className?: string
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  return (
    <nav aria-label="面包屑" className={cn('text-sm', className)}>
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((item, index) => {
          const last = index === items.length - 1
          return (
            <Fragment key={item.key}>
              {index > 0 ? (
                <ChevronRight className="size-3.5 text-muted" aria-hidden />
              ) : null}
              <li>
                {last || (!item.href && !item.onClick) ? (
                  <span
                    className={cn(
                      last ? 'font-medium text-foreground' : 'text-muted',
                    )}
                    aria-current={last ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                ) : item.href ? (
                  <a
                    href={item.href}
                    className="text-muted transition-colors duration-150 hover:text-foreground"
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="text-muted transition-colors duration-150 hover:text-foreground"
                    onClick={item.onClick}
                  >
                    {item.label}
                  </button>
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
